import moment from 'moment';
import React, { useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';

import { OccurrenceCard } from 'features/Responder/features/Schedules/features/Assignments/components/AssignmentBuilder/components/OccurrenceCard';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'styles/dark-calendar.css';
import { getScheduleEventProps } from 'utils/recurrenceUtils';
import { CalendarToolbar } from './components/CalendarToolbar';
import './index.css';

const localizer = momentLocalizer(moment);

/**
 * For each day that has more than one event, mark exactly one event as the
 * "representative" (earliest start time, stable sort) and record the count.
 * Returns a Map<eventObject, { isRepresentative: boolean, count: number }>.
 * Days with only one event are not included – they render normally.
 *
 * Using object-reference keys means the lookup in the event component is O(1)
 * and never fails due to time coincidences or calendar render-order differences.
 */
function buildDayBehaviorMap(events) {
  const dayGroups = {};
  events.forEach((event) => {
    const key = moment(event.start).format('YYYY-MM-DD');
    if (!dayGroups[key]) dayGroups[key] = [];
    dayGroups[key].push(event);
  });
  const behaviorMap = new Map();
  Object.values(dayGroups).forEach((dayEvents) => {
    if (dayEvents.length <= 1) return;
    const sorted = [...dayEvents].sort((a, b) => a.start - b.start || a.end - b.end);
    sorted.forEach((event, i) => {
      behaviorMap.set(event, { isRepresentative: i === 0, count: dayEvents.length });
    });
  });
  return behaviorMap;
}

const ScheduleCalendar = ({
  events,
  showDaily = false,
  onSelectEvent = () => {},
  showAgenda = false,
  showToolbar = true,
  onView,
  onRangeChange,
  eventPropGetter = getScheduleEventProps,
  selectedEvent,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  filterString = '',
  debouncedFilterString,
  setFilterString = () => {},
  height = 0,
  isHydratingEvents = false,
  collapseDuplicateDays = true,
  showAssigner = false,
}) => {
  const [cellScale, setCellScale] = useState(5);
  const [currentView, setCurrentView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());

  // Map<event, { isRepresentative, count }>; recomputed whenever the events list changes.
  const dayBehaviorMap = React.useMemo(() => buildDayBehaviorMap(events ?? []), [events]);

  const handleSelectEvent = (event) => {
    if (onSelectEvent) {
      onSelectEvent(event);
    }
  }; // Use percentage scaling
  const dayCellHeight = 1200 * cellScale;

  const handleViewChange = (view) => {
    setCurrentView(view);
    if (onView) {
      onView(view);
    }
  };

  return (
    <div
      className={`schedule-calendar dark-calendar d-flex flex-column h-100 flex-grow-1 position-relative ${
        isHydratingEvents ? 'schedule-calendar--hydrating' : ''
      }`}
      style={{ minHeight: 0 }}
      aria-busy={isHydratingEvents}
    >
      {showToolbar && (
        <CalendarToolbar
          cellScale={cellScale}
          setCellScale={setCellScale}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          filterString={filterString}
          setFilterString={setFilterString}
          debouncedFilterString={debouncedFilterString}
        />
      )}
      <style>{`
        .rbc-time-view .rbc-day-slot { min-height: ${dayCellHeight}px; height: ${dayCellHeight}px;  box-sizing: border-box;}
        .rbc-time-slot { min-height: ${dayCellHeight / 48}px; height: ${dayCellHeight / 24}px; box-sizing: border-box; }
        
        /* Hide default calendar event styling to show only OccurrenceCard */
        .rbc-event {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        .rbc-event.rbc-selected {
          background: transparent !important;
        }
        .rbc-event-label {
          display: none !important;
        }
        .rbc-event-content {
          padding: 0 !important;
          margin: 0 !important;
        }
      `}</style>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        className="d-flex flex-grow-1 h-100"
        style={{ minHeight: height }}
        views={['month', 'week', showDaily ? 'day' : null, showAgenda ? 'agenda' : null].filter(
          Boolean,
        )}
        defaultView="month"
        view={currentView}
        date={currentDate}
        onView={handleViewChange}
        onNavigate={(date) => setCurrentDate(date)}
        onRangeChange={onRangeChange}
        onSelectEvent={handleSelectEvent}
        dayLayoutAlgorithm={'no-overlap'}
        showNowIndicator={false}
        popup={false}
        eventPropGetter={eventPropGetter}
        components={{
          showMore: () => null,
          event: ({ event }) => {
            // In month view, collapse days with multiple occurrences into a single button.
            if (collapseDuplicateDays && currentView === 'month') {
              const behavior = dayBehaviorMap.get(event);
              if (behavior) {
                if (!behavior.isRepresentative) return null;
                return (
                  <div
                    role="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentDate(event.start);
                      handleViewChange('day');
                    }}
                    className="d-flex align-items-center justify-content-center rounded-1 p-1 m-1"
                    style={{
                      background: '#325277',
                      cursor: 'pointer',
                    }}
                  >
                    {behavior.count} Events
                  </div>
                );
              }
            }
            return (
              <OccurrenceCard
                event={event}
                source={'calendar'}
                showAssigner={showAssigner}
                calendarProps={{
                  calendarView: currentView,
                  handleViewChange: handleViewChange,
                  setCurrentDate: setCurrentDate,
                  handleSelectEvent: handleSelectEvent,
                }}
                isSelected={
                  selectedEvent &&
                  event?.start?.getTime() === selectedEvent?.start?.getTime() &&
                  event?.end?.getTime() === selectedEvent?.end?.getTime()
                }
                opacity={Date.now() > new Date(event.end) ? 0.6 : 1}
              />
            );
          },
        }}
      />
      {isHydratingEvents && (
        <div className="schedule-calendar__loading-hint">Loading events...</div>
      )}
    </div>
  );
};

export default ScheduleCalendar;
